'use client';
import { useContext, useState } from 'react';
import { FormikHelpers, FormikErrors } from 'formik';
import toast from 'react-hot-toast';
import parsePhoneNumber from 'libphonenumber-js';

import cn from 'classnames';

import Form, { BaseFormProps } from '@/components/form/lib/Form/Form';
import CheckboxField from '@/components/form/lib/InputField/CheckboxField';
import Button from '@/components/common/Button/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import Link from '@/components/common/Link/Link';
import EmailField from '@/components/form/lib/EmailField/EmailField';
import MaskedField from '@/components/form/lib/InputField/MaskedField';

import { phoneMaskPipe } from '@/util/phone';
import { useLoggedInUser } from '@/components/hook/user';
import { SubscribeToNewsletterSMSRequest } from '@/app/api/marketing/newsletter-and-sms/route';

import { setSMSOptIn } from '@/util/user_client';
import { isValidEmail } from '@/util/regexp';
import analytics from '@/util/analytics';

import { CACHE_TIME } from '@/const/cache';
import { SMS_OPTIN_COPY } from '@/const/sms_optin_copy';

import { UserContext } from '@/components/context/UserContext/UserContext';
import { useCloset } from '@/components/hook/closet';

import './EmailSmsModalForm.style.scss';
import {
  emailValidationError,
  phoneOptInValidationError
} from '@/util/form_validation_errors';

export type EmailSmsModalFormValues = {
  email: string;
  optinText: boolean;
  phoneNumber: string;
};

interface EmailSmsModalFormProps
  extends Omit<
    BaseFormProps<EmailSmsModalFormValues>,
    'children' | 'onSubmit'
  > {
  onSubmit?: (
    values: EmailSmsModalFormValues,
    actions: FormikHelpers<EmailSmsModalFormValues>
  ) => void;
}

async function defaultValidation(
  values: EmailSmsModalFormValues
): Promise<FormikErrors<EmailSmsModalFormValues>> {
  const { email, optinText, phoneNumber } = values;
  const errors: FormikErrors<EmailSmsModalFormValues> = {};
  const emailError = await emailValidationError(email);
  const phoneError = phoneOptInValidationError(optinText, phoneNumber);

  if (!email) {
    errors.email = 'An email address is required';
  } else if (!isValidEmail(email)) {
    errors.email = emailError;
  }

  if (phoneError) {
    errors.phoneNumber = phoneError;
  }

  return errors;
}

export default function EmailSmsModalForm(props: EmailSmsModalFormProps) {
  const {
    className = '',
    initialValues,
    validate = defaultValidation,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    validateOnBlur = true,
    validateOnChange = false
  } = props;
  const user = useLoggedInUser();
  const userContext = useContext(UserContext);
  const {
    value: { closet }
  } = useCloset();

  const [isLoading, setIsLoading] = useState(false);

  function handleAnalytics(event: string) {
    analytics.track(event, {
      membership_tier: closet?.subscriptionPlanProducts[0]?.name,
      add_ons: !!closet?.subscriptionPlanProducts[1]?.name,
      insurance_coverage: closet?.insurancePlanProducts[0]?.name
    });
  }

  const handleSubmit = async (values: EmailSmsModalFormValues) => {
    let body: SubscribeToNewsletterSMSRequest;

    handleAnalytics('Email/SMS Pop-up Subscribe - Submitted');

    setIsLoading(true);

    // If user opted into SMS, send to Braze
    if (values.phoneNumber && values.optinText) {
      body = {
        email: values.email,
        phone: parsePhoneNumber(values.phoneNumber, 'US')?.format('E.164'),
        source: 'email-sms-modal',
        optinText: values.optinText
      };

      if (user) {
        body.existingUser = {
          id: user.id,
          email: user.email,
          phone: parsePhoneNumber(
            user.phone || values.phoneNumber,
            'US'
          )?.format('E.164')
        };
      }

      await setSMSOptIn(true, 'email-sms-modal');
    } else {
      body = {
        email: values.email,
        phone: values.phoneNumber,
        source: 'email-sms-modal'
      };

      if (user) {
        body.existingUser = {
          id: user.id,
          email: user.email
        };
      }
    }

    try {
      const response = await fetch('/api/marketing/newsletter-and-sms', {
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify(body),
        next: { revalidate: CACHE_TIME.NO_CACHE }
      });
      const respJson = await response.json();

      if (!respJson.success) {
        toast.error('An error occurred. Please try again');
        onSubmitFailure?.(values);
      }

      if (user) {
        await userContext.actions.update({
          subscriptions: {
            email: !!values.email,
            sms: values.optinText
          }
        });
      }

      toast.success("Thanks! You've been added to our mailing list");
      onSubmitSuccess?.(values);
    } catch {
      toast.error('An error occurred. Please try again');
      setIsLoading(true);
      onSubmitFailure?.(values);
    }
  };

  return (
    <Form<EmailSmsModalFormValues>
      className={cn('EmailSmsModalForm', className)}
      initialValues={initialValues}
      validate={validate}
      // If an 'onSubmit function' is passed in, that function will override
      // this component's 'handleSubmit' function.
      onSubmit={onSubmit || handleSubmit}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
    >
      <EmailField
        name='email'
        label={'Email'}
        onFocus={() => handleAnalytics('Email/SMS Pop-up Email - Viewed')}
      />
      <MaskedField
        mask={[
          '(',
          /[1-9]/,
          /\d/,
          /\d/,
          ')',
          ' ',
          /\d/,
          /\d/,
          /\d/,
          '-',
          /\d/,
          /\d/,
          /\d/,
          /\d/
        ]}
        pipe={phoneMaskPipe}
        label={'Phone Number'}
        name='phoneNumber'
        guide={false}
        type='tel'
        autoComplete='tel'
        placeholder={'(xxx) xxx-xxxx'}
        onFocus={() =>
          handleAnalytics('Email/SMS Pop-up Phone Number - Viewed')
        }
      />

      <div className='InputField-checkbox'>
        <CheckboxField
          name='optinText'
          label={
            <span>
              {SMS_OPTIN_COPY}{' '}
              <a
                href='/terms'
                className='Signup-terms'
                target='_blank'
              >
                Terms & Conditions.
              </a>
            </span>
          }
        />
      </div>

      <div className='EmailSmsModalForm-button-container'>
        <Button
          type='submit'
          className='EmailSmsModalForm-button'
        >
          {isLoading ? <LoadingSpinner size={22} /> : 'Subscribe'}
        </Button>

        <p className='EmailSmsModalForm-privacy'>
          <Link
            href='/privacy-policy'
            newTab={true}
            compact
            underline
            onClick={() =>
              handleAnalytics('Email/SMS Pop-up Privacy Policy - Clicked')
            }
          >
            View our Privacy Policy
          </Link>
        </p>
      </div>
    </Form>
  );
}
